import { useEffect, useMemo } from 'react';
import urlTemplate from 'url-template';
import type { MyTransactionsRequest } from 'src/apis/capacity-management/use-my-transactions';
import type {
  ReshuffleActionsResponse,
  SearchAllocationsPayload,
} from 'src/apis/reshuffle-service/types';
import type { OptionalReshuffleServiceRootRequest } from 'src/apis/reshuffle-service/use-reshuffle-service';
import { getAllocationIds } from 'src/apis/reshuffle-service/utils';
import { useAxios } from 'src/hooks/use-axios';

type ExecuteProps = {
  url: string;
  /* Only set for the searchAllocations link, which expects the ids as a payload.
  The templated getAllocations link carries them in the url instead. */
  payload?: SearchAllocationsPayload;
};

export function useReshuffleAllocationActions(
  myTransactionsRequest: MyTransactionsRequest,
  reshuffleServiceRoot: OptionalReshuffleServiceRootRequest
) {
  const request = useAxios(
    (axios, baseConfig, { url, payload }: ExecuteProps) =>
      axios.request<ReshuffleActionsResponse>({
        ...baseConfig,
        url,
        ...(payload && { method: 'POST', data: payload }),
      }),
    { neededOnPageLoad: false }
  );

  const executeProps = useMemo(() => {
    const links = reshuffleServiceRoot?.response?.data._links;
    const items = myTransactionsRequest.response?.data._embedded?.items;

    if (!items) {
      return;
    }

    const ids = items.flatMap(getAllocationIds);

    if (ids.length === 0) {
      return;
    }

    /* A page holds up to 50 transactions with two allocation ids each, which no longer fits into the
    query parameter of the getAllocations link.
    TODO: Remove the getAllocations fallback once searchAllocations is deployed to all stages. */
    if (links?.searchAllocations) {
      return { url: links.searchAllocations.href, payload: { ids } };
    }

    if (links?.getAllocations) {
      return {
        url: urlTemplate.parse(links.getAllocations.href).expand({ ids }),
      };
    }
  }, [myTransactionsRequest.response, reshuffleServiceRoot?.response]);

  /* Refreshing the transactions table gives us a new response object, so the request is keyed on
  its content to not re-fetch the actions for allocations we already asked about. */
  const executeKey =
    executeProps &&
    [executeProps.url, ...(executeProps.payload?.ids ?? [])].join(',');

  useEffect(() => {
    if (!executeProps) {
      return;
    }

    request.execute(executeProps);
    // oxlint-disable-next-line exhaustive-deps
  }, [executeKey]);

  return request;
}

export type ReshuffleAllocationActionsRequest = ReturnType<
  typeof useReshuffleAllocationActions
>;
